/**
 * The chat line — a real `<input>`, opened on demand and closed the moment it
 * is done with.
 *
 * It is a DOM field rather than a canvas-drawn prompt for the reasons the log
 * pane is DOM: a text field the browser owns gets a caret, selection, an IME,
 * autocorrect on a phone, and a screen reader that knows what it is. A prompt
 * painted into a glyph grid gets none of that and has to reimplement the parts
 * it can.
 *
 * Focus is the whole design here. While the line is open it has the keyboard
 * and movement keys type letters; the instant it closes, the keyboard goes
 * back to the game. Nothing else on screen ever takes focus, which is what
 * makes that switch unambiguous — see `LogPane`, which pointedly does not.
 */

import { MAX_SAY_LENGTH } from '@annwn/protocol';

export interface ChatLineHandlers {
  /** A line the player committed. Already trimmed; never empty. */
  onSubmit(text: string): void;

  /** Opened or closed. The game suspends held input while it is open. */
  onToggle?(open: boolean): void;
}

export class ChatLine {
  private readonly form: HTMLFormElement;
  private readonly field: HTMLInputElement;
  private readonly handlers: ChatLineHandlers;

  constructor(form: HTMLFormElement, field: HTMLInputElement, handlers: ChatLineHandlers) {
    this.form = form;
    this.field = field;
    this.handlers = handlers;

    // The server caps it too (`MAX_SAY_LENGTH` is enforced in the sim). This
    // is courtesy, not security: the player sees the limit as they reach it
    // rather than discovering it in the log afterwards.
    this.field.maxLength = MAX_SAY_LENGTH;

    this.form.addEventListener('submit', this.onSubmit);
    this.field.addEventListener('keydown', this.onKeyDown);
    this.close();
  }

  get open(): boolean {
    return !this.form.hidden;
  }

  toggle(): void {
    if (this.open) this.close();
    else this.show();
  }

  show(): void {
    if (this.open) return;
    this.form.hidden = false;
    this.field.value = '';
    this.field.focus();
    this.handlers.onToggle?.(true);
  }

  close(): void {
    const wasOpen = this.open;
    this.form.hidden = true;
    this.field.value = '';
    // Hand the keyboard back deliberately rather than leaving it on a hidden
    // field, which is a state where nothing at all responds to a keypress.
    if (wasOpen) this.field.blur();
    if (wasOpen) this.handlers.onToggle?.(false);
  }

  dispose(): void {
    this.form.removeEventListener('submit', this.onSubmit);
    this.field.removeEventListener('keydown', this.onKeyDown);
  }

  private readonly onSubmit = (event: Event): void => {
    event.preventDefault();

    const text = this.field.value.trim();
    // Closing on an empty Enter is what a player means by it — the alternative
    // is a field they have to press Escape to be rid of.
    this.close();
    if (text.length > 0) this.handlers.onSubmit(text);
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    // Stop it here: the same key cancels look mode, and one press should not
    // do two things.
    event.preventDefault();
    event.stopPropagation();
    this.close();
  };
}
